/**
 * DebuggerPanel Component
 * Time-travel debugger with pause/resume/rewind and state inspection
 * Redesigned for better UX and clarity
 */

import React, { useState } from 'react';
import { useDashboardStore } from '../store/dashboardStore';
import { useAPI } from '../hooks/useAPI';
import {
  ChevronUp,
  ChevronDown,
  Play,
  Pause,
  RotateCcw,
  Copy,
  Maximize2,
  Minimize2,
  Terminal,
  Activity,
} from 'lucide-react';
import toast from 'react-hot-toast';

const StatRow = ({ label, value, valueClass = 'text-amber-400' }: { label: string; value: React.ReactNode; valueClass?: string }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-gray-700/50 last:border-0">
    <span className="text-xs text-gray-400">{label}</span>
    <span className={`text-xs font-bold font-mono ${valueClass}`}>{value}</span>
  </div>
);

export const DebuggerPanel: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const isPaused = useDashboardStore((state) => state.isPaused);
  const events = useDashboardStore((state) => state.events);
  const { pauseDebugger, resumeDebugger } = useAPI({ enableSocket: false });

  const mockState = {
    metrics: useDashboardStore((state) => state.metrics),
    connectionStatus: useDashboardStore((state) => state.connectionStatus),
    agents: useDashboardStore((state) => state.agents),
    eventCount: events.length,
  };

  const handlePause = async () => {
    try {
      await pauseDebugger();
      toast.success('System paused — no new requests will be processed');
    } catch {
      toast.error('Failed to pause');
    }
  };

  const handleResume = async () => {
    try {
      await resumeDebugger();
      toast.success('System resumed');
    } catch {
      toast.error('Failed to resume');
    }
  };

  const handleCopyState = () => {
    navigator.clipboard.writeText(JSON.stringify(mockState, null, 2));
    toast.success('State snapshot copied to clipboard');
  };

  const containerClass = isMaximized
    ? 'fixed inset-0 z-50 rounded-none'
    : isOpen
    ? 'h-72'
    : 'h-auto';

  return (
    <div
      className={`bg-gray-950 text-gray-100 border-t border-gray-800 flex flex-col ${containerClass} transition-all duration-300`}
    >
      {/* Panel Toggle Header */}
      <div className="px-5 py-2.5 border-b border-gray-800/80 flex items-center justify-between bg-gray-900/60 backdrop-blur-sm flex-shrink-0">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2.5 hover:text-indigo-300 transition-colors flex-1 group"
        >
          {isOpen ? (
            <ChevronDown className="w-4 h-4 text-gray-500 group-hover:text-indigo-400" />
          ) : (
            <ChevronUp className="w-4 h-4 text-gray-500 group-hover:text-indigo-400" />
          )}
          <Terminal className="w-3.5 h-3.5 text-gray-500" />
          <span className="text-xs font-bold text-gray-400 group-hover:text-indigo-300 tracking-wide">
            Developer Tools
          </span>
          <span className="text-xs text-gray-600">· Time-Travel Debugger</span>

          {/* Running/Paused status */}
          <span
            className={`ml-2 text-xs px-2.5 py-0.5 rounded-full font-bold border ${
              isPaused
                ? 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                : 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
            }`}
          >
            {isPaused ? '⏸ PAUSED' : '▶ RUNNING'}
          </span>
        </button>

        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsMaximized(!isMaximized)}
            className="p-1.5 hover:bg-gray-800 rounded-lg transition text-gray-500 hover:text-gray-200"
            title={isMaximized ? 'Minimize panel' : 'Expand panel'}
          >
            {isMaximized ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Panel Content */}
      {isOpen && (
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-3 gap-3 p-4">

            {/* Playback Controls */}
            <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
              <h3 className="text-xs font-bold uppercase text-gray-500 tracking-wider mb-3 flex items-center gap-1.5">
                <Activity className="w-3 h-3" />
                Playback Control
              </h3>

              <div className="flex items-center gap-2 mb-3">
                <button
                  onClick={isPaused ? handleResume : handlePause}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    isPaused
                      ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                      : 'bg-amber-600 hover:bg-amber-500 text-white'
                  }`}
                  title={isPaused ? 'Resume event processing' : 'Pause event processing'}
                >
                  {isPaused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
                  {isPaused ? 'Resume' : 'Pause'}
                </button>

                <button
                  onClick={() => {
                    const allEvents = useDashboardStore.getState().events;
                    if (allEvents.length > 0) {
                      toast.success('Rewound — inspect the event stream below');
                    } else {
                      toast.error('No events to step through');
                    }
                  }}
                  className="p-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 transition text-gray-300"
                  title="Step backward one event"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>

                <select
                  value={playbackSpeed}
                  onChange={(e) => setPlaybackSpeed(Number(e.target.value))}
                  className="ml-auto text-xs bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-gray-300 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  title="Playback speed multiplier"
                >
                  <option value={0.5}>0.5× speed</option>
                  <option value={1}>1× speed</option>
                  <option value={2}>2× speed</option>
                  <option value={5}>5× speed</option>
                </select>
              </div>

              <p className="text-xs text-gray-500 leading-relaxed">
                {isPaused
                  ? '⏸ Processing is paused. No new events will be accepted until you resume.'
                  : '▶ System is running normally. Pause to freeze the event stream for inspection.'}
              </p>
            </div>

            {/* State Inspector */}
            <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-bold uppercase text-gray-500 tracking-wider">
                  Current State
                </h3>
                <button
                  onClick={handleCopyState}
                  className="p-1.5 hover:bg-gray-800 rounded-lg transition text-gray-500 hover:text-gray-200"
                  title="Copy full state as JSON"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>

              <div className="space-y-0.5">
                <StatRow
                  label="Total Requests"
                  value={mockState.metrics?.totalRequests ?? '—'}
                />
                <StatRow
                  label="Cache Hits"
                  value={mockState.metrics?.cacheHits ?? '—'}
                  valueClass="text-emerald-400"
                />
                <StatRow
                  label="Hit Rate"
                  value={mockState.metrics ? `${((mockState.metrics.hitRate || 0) * 100).toFixed(1)}%` : '—'}
                  valueClass="text-indigo-400"
                />
                <StatRow
                  label="Active Agents"
                  value={mockState.agents?.length ?? '—'}
                  valueClass="text-blue-400"
                />
                <StatRow
                  label="Events Logged"
                  value={mockState.eventCount}
                  valueClass="text-gray-300"
                />
                <StatRow
                  label="Connection"
                  value={mockState.connectionStatus}
                  valueClass={mockState.connectionStatus === 'connected' ? 'text-emerald-400' : 'text-red-400'}
                />
              </div>
            </div>

            {/* Event Position Scrubber */}
            <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
              <h3 className="text-xs font-bold uppercase text-gray-500 tracking-wider mb-3">
                Event Timeline Position
              </h3>

              <div className="flex items-center gap-2 mb-3">
                <button className="px-2.5 py-1.5 text-xs font-bold bg-gray-800 hover:bg-gray-700 rounded-lg transition text-gray-300">
                  ← Prev
                </button>
                <button className="px-2.5 py-1.5 text-xs font-bold bg-gray-800 hover:bg-gray-700 rounded-lg transition text-gray-300">
                  Next →
                </button>
              </div>

              <div className="w-full bg-gray-800 rounded-full h-2 mb-2 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-indigo-500 to-violet-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: events.length > 0 ? `${Math.min((events.length / 100) * 100, 100)}%` : '0%' }}
                />
              </div>

              <p className="text-xs text-gray-500">
                {events.length} events recorded
              </p>
              <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                Scrub through events to inspect system state at any point in time.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
